import L from 'leaflet';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import useLeafletLayer from './useLeafletLayer';

function LeafletMarker(props) {
  const {
    position,
    icon,
    opacity,
    zIndexOffset,
    draggable,
    bubblingMouseEvents,
    interactive,
    tooltip,
    eventHandlers,
  } = props;

  // An option passed as undefined would override Leaflet's default (interactive: true, say)
  const marker = useLeafletLayer(
    () => L.marker(
      position,
      Object.fromEntries(Object.entries({
        icon,
        opacity,
        zIndexOffset,
        draggable,
        bubblingMouseEvents,
        interactive,
      }).filter(([, value]) => (value !== undefined))),
    ),
    eventHandlers,
  );

  useEffect(
    () => {
      if (marker?.dragging && (draggable !== undefined)) {
        if (draggable) marker.dragging.enable();
        else marker.dragging.disable();
      }
    },
    [marker, draggable],
  );

  useEffect(
    () => {
      if (marker) marker.setLatLng(position);
    },
    [marker, position],
  );

  useEffect(
    () => {
      if (marker && icon) marker.setIcon(icon);
    },
    [marker, icon],
  );

  useEffect(
    () => {
      if (marker && (opacity !== undefined)) marker.setOpacity(opacity);
    },
    [marker, opacity],
  );

  useEffect(
    () => {
      if (marker && (zIndexOffset !== undefined)) marker.setZIndexOffset(zIndexOffset);
    },
    [marker, zIndexOffset],
  );

  useEffect(
    () => {
      if (!marker) return undefined;
      if (tooltip) marker.bindTooltip(tooltip);
      return () => {
        marker.unbindTooltip();
      };
    },
    [marker, tooltip],
  );

  return null;
}

const latLngShape = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.number),
  PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
]);

LeafletMarker.propTypes = {
  position: latLngShape.isRequired,
  icon: PropTypes.shape({}),
  opacity: PropTypes.number,
  zIndexOffset: PropTypes.number,
  draggable: PropTypes.bool,
  // false keeps a click on the marker from reaching the map
  bubblingMouseEvents: PropTypes.bool,
  // false lets mouse events through to whatever is under the marker
  interactive: PropTypes.bool,
  // Leaflet's own hover tooltip, for markers whose DOM the app does not render
  tooltip: PropTypes.string,
  eventHandlers: PropTypes.shape({}),
};

export default LeafletMarker;
